import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAuth0 } from '@auth0/auth0-angular';
import { provideNgxMask } from 'ngx-mask';

import { routes } from './app.routes';
import { CacheInterceptor } from './core/interceptor/caching.interceptor';
import { AuthInterceptor } from './core/interceptor/authorization.interceptor';
import { MaintenanceInterceptor } from './core/interceptor/maintenance.interceptor';
import { AppInsightsService } from './core/services/app-insights.service';
import { ThemeService } from './core/services/theme.service';
import { DropdownPortalService } from './core/services/dropdown-portal.service';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { AuthCallbackConstants } from './core/constants/constants';
import { environment } from '../environments/environment';

export const appConfig = (): ApplicationConfig => ({
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxMask(),
    { provide: HTTP_INTERCEPTORS, useClass: MaintenanceInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAppInitializer(() => {
      inject(AppInsightsService);
      // Eagerly apply the saved/system theme so it works on every route,
      // including bare routes with no layout (e.g. the system error pages).
      inject(ThemeService);
      // One document-level listener keeps every dropdown inside a scrollable table out of that
      // table's overflow clipping, so a new list never has to opt in to it.
      inject(DropdownPortalService).start();
    }),
    provideAuth0({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri: window.location.origin + '/dashboard',
        audience: environment.auth0Audience,
      },
      cacheLocation: 'memory',
      // A failed sign-in callback (e.g. a deactivated user, whom Auth0 refuses as blocked) lands here
      // instead of "/", where AuthGuard would silently start another login.
      errorPath: '/' + AuthCallbackConstants.ERROR_PATH(),
    })
  ]
});
